import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import {useHistory} from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import {forgotPassword, forgotPasswordSubmit, resendEmailConfirmationCode} from '../../redux/slices/auth';
import SecondaryAppBar from '../../lesson-builder/components/SecondayAppBar';
import {BluePillButton} from '../../common/components/BluePillButton';
import {CONFIRMATION_CODE_LENGTH, PASSWORD_MIN_LENGTH} from '../../constants';

import baseStyles from '../../css/Base.module.css';
import {isValidEmail} from '../../helpers';

const STEP = {
  EMAIL: 'EMAIL',
  PASSWORD: 'PASSWORD',
};

export default function ForgotPassword() {
  const [step, setStep] = useState(STEP.EMAIL);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [dialogMessage, setDialogMessage] = useState(null);

  const dispatch = useDispatch();
  const history = useHistory();

  function handleEmailChanged(event) {
    setEmail(event.target.value);
  }
  function handleCodeChanged(event) {
    setCode(event.target.value);
  }
  function handlePasswordChanged(event) {
    setPassword(event.target.value);
  }

  function isSubmitButtonEnabled() {
    return step === STEP.EMAIL
      ? !!email && isValidEmail(email)
      : !!code && code.length >= CONFIRMATION_CODE_LENGTH && !!password;
  }

  function validateForm() {
    const errors = {};

    if (step === STEP.EMAIL) {
      if (!email) errors.email = 'This field is required.';
      else if (!isValidEmail(email)) errors.email = 'Invalid email address.';
    } else if (step === STEP.PASSWORD) {
      if (!code) errors.code = 'This field is required.';
      else if (code.length < CONFIRMATION_CODE_LENGTH) errors.code = 'Invalid code.';

      if (!password) errors.password = 'This field is required.';
      else if (password.length < PASSWORD_MIN_LENGTH)
        errors.password = `Use ${PASSWORD_MIN_LENGTH} or more characters with a mix of uppercase letters, lowercase letters, and numbers.`;
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  }

  async function handleClickResendCode() {
    try {
      await dispatch(resendEmailConfirmationCode(email));
    } catch {
      setDialogMessage('An error occurred, try again.');
    }
  }

  async function handleSubmitForm(event) {
    event.preventDefault();

    if (step === STEP.EMAIL) submitEmail();
    else if (step === STEP.PASSWORD) submitPassword();
  }

  async function submitEmail() {
    const isFormValid = validateForm();

    if (isFormValid) {
      let response;

      try {
        response = await dispatch(forgotPassword(email));
      } catch (error) {
        console.log(`forgot password error: ${JSON.stringify(error)}`);

        const {code} = error;
        if (code === 'UserNotFoundException') {
          setDialogMessage('Verify your email address is correct.');
        } else if (code === 'LimitExceededException') {
          setDialogMessage('Attempt limit exceeded. Try again later.');
        } else {
          setDialogMessage('An error occurred, try again.');
        }
      }

      if (response?.CodeDeliveryDetails) {
        setStep(STEP.PASSWORD);
      }
    }
  }

  async function submitPassword() {
    const isFormValid = validateForm();

    if (isFormValid) {
      try {
        await dispatch(forgotPasswordSubmit(email, code, password));
      } catch (error) {
        console.log(`forgot password submit error: ${JSON.stringify(error)}`);

        const {code, message} = error;
        if (code === 'CodeMismatchException') {
          setFormErrors({...formErrors, code: 'Invalid code, please try again.'});
        } else if (code === 'ExpiredCodeException') {
          setFormErrors({...formErrors, code: 'Code expired, request a new one.'});
        } else if (code === 'InvalidPasswordException' && message.includes('Password does not conform to policy')) {
          setFormErrors({
            ...formErrors,
            password: `Use ${PASSWORD_MIN_LENGTH} or more characters with a mix of uppercase letters, lowercase letters, and numbers.`,
          });
        } else {
          setDialogMessage('An error occurred, try again.');
        }
      }

      history.push('/login');
    }
  }

  const handleClickShowPassword = () => {
    setPasswordVisible(!isPasswordVisible);
  };
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  function renderDialog() {
    return (
      <Dialog open={!!dialogMessage} onClose={() => setDialogMessage(null)}>
        <DialogTitle>Forgot Password</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogMessage(null)} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return <>
    <AppBar position="sticky" className="bg-brandorange">
      <Toolbar variant="dense">
        <Typography variant="h6" className={baseStyles['f-g-1']}>
          Flamingo Learning
        </Typography>
      </Toolbar>
    </AppBar>
    <SecondaryAppBar title="Forgot Password" />

    {renderDialog()}

    <form className="p-20" onSubmit={handleSubmitForm}>
      <Grid container spacing={3} item xl={3} lg={4} md={5} sm={7}>
        {step === STEP.EMAIL && (
          <>
            <Typography variant="body1" className="clr-gray-400 p-t-10 p-lr-10">
              Enter the email address associated with your account.
            </Typography>
            <Grid item xs={12} className="m-b-10">
              <TextField
                variant="standard"
                margin="dense"
                label="Email"
                onChange={handleEmailChanged}
                value={email}
                error={!!formErrors.email}
                helperText={formErrors.email}
                autoFocus
                fullWidth
              />
            </Grid>

            <Grid item xs={6}>
              <Button onClick={() => history.push('/login')}>CANCEL</Button>
            </Grid>
          </>
        )}

        {step === STEP.PASSWORD && (
          <>
            <Typography variant="body1" className="clr-gray-400 p-lr-10">
              A confirmation code was sent to your email address.
            </Typography>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Confirmation code"
                onChange={handleCodeChanged}
                value={code}
                error={!!formErrors.code}
                helperText={formErrors.email}
                autoFocus
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} className="m-b-10">
              <FormControl fullWidth required>
                <InputLabel htmlFor="login-form-new-password">New password</InputLabel>
                <Input
                  id="login-form-new-password"
                  className="m-t-10"
                  value={password}
                  onChange={handlePasswordChanged}
                  type={isPasswordVisible ? 'text' : 'password'}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        size="large">
                        {isPasswordVisible ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  }
                  error={!!formErrors.password}
                />
                {!!formErrors.password ? (
                  <FormHelperText error>{formErrors.password}</FormHelperText>
                ) : (
                  <FormHelperText>{`Use ${PASSWORD_MIN_LENGTH} or more characters with a mix of uppercase letters, lowercase letters, and numbers.`}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <Button onClick={handleClickResendCode}>Send new code</Button>
            </Grid>
          </>
        )}

        <Grid item xs={6} className={baseStyles['text-align-end']}>
          <BluePillButton type="submit" variant="outlined" disabled={!isSubmitButtonEnabled()}>
            SUBMIT
          </BluePillButton>
        </Grid>
      </Grid>
    </form>
  </>;
}
